import { fmtDayMonth } from '../../../flight/results/flight_format.js';
import { productContextLabels, type CheckoutContext } from '../../drawer/checkout_context.js';
import type { CheckoutEnv } from '../../model/checkout_env.js';
import type { FlightItemSource } from '../../model/checkout_source.js';

const DAY_MS = 86_400_000;
const dayOf = (iso: string): string => iso.slice(0, 10);

/** Faixa curta do roteiro — "17 – 22 set" quando o mês/ano coincidem, senão as
 *  duas pontas por extenso curto ("28 ago – 3 set"); o ano, fora do corrente, só
 *  na última. É a régua do Figma
 *  (405-5518) pra meta line e pro chip do topbar; a forma longa do
 *  `fmtTripDates` fica pro planner. */
function fmtShortRange(outDay: string, retDay: string | undefined, locale: string): string {
  if (!retDay || retDay === outDay) return fmtDayMonth(outDay, locale);
  const out = fmtDayMonth(outDay, locale, []);
  const ret = fmtDayMonth(retDay, locale, [outDay, retDay]);
  const sameMonthYear = outDay.slice(0, 7) === retDay.slice(0, 7);
  const outDayNumber = String(Number(outDay.slice(8, 10)));
  return sameMonthYear ? `${outDayNumber} – ${ret}` : `${out} – ${ret}`;
}

/** O contexto do carrinho de voo para o chrome do takeover e o cartão "Sua
 *  viagem": rota, cidade, janela de datas e noites, só do que o fio já carrega
 *  (spec: front-only). O multi-trecho vai da partida do primeiro trecho à do
 *  último. */
export function flightCheckoutContext(
  items: readonly FlightItemSource[],
  env: Pick<CheckoutEnv, 'copy' | 'locale' | 'cityOf'>,
): CheckoutContext {
  const { locale } = env;
  const cityOf = (iata: string): string => env.cityOf(iata) ?? iata;
  const base = productContextLabels(env.copy, 'flight');
  const first = items[0];
  const last = items.at(-1);
  if (!first || !last) return base;
  const outDay = dayOf(first.detail.outbound.departAt);
  // RT: a volta do próprio item; multi-trecho: a partida do último trecho.
  let retDay: string | undefined;
  if (first.detail.return) {
    retDay = dayOf(first.detail.return.departAt);
  } else if (items.length > 1) {
    retDay = dayOf(last.detail.outbound.departAt);
  }
  const destination = first.detail.return ? first.detail.outbound.to : last.detail.outbound.to;
  // 'YYYY-MM-DD' parseia em UTC nas duas pontas — a diferença de dias é exata.
  const nights = first.detail.return
    ? Math.round((Date.parse(dayOf(first.detail.return.departAt)) - Date.parse(outDay)) / DAY_MS)
    : 0;
  return {
    ...base,
    route: `${cityOf(first.detail.outbound.from)} → ${cityOf(destination)}`,
    city: cityOf(destination),
    dates: fmtShortRange(outDay, retDay, locale),
    ...(nights > 0 ? { nights } : {}),
  };
}
